import { OpDrawer } from "components/customAntd/DLS/OpDrawer/OpDrawer";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { OpTabs } from "components/customAntd/DLS/OpTabs/OpTabs";
import { useState, useEffect } from "react";
import { useConfirmModal } from "utils/customHooks/useConfirmModal";
import { DRAWER_WIDTH } from "constants/ui";
import InfoContent from "./tabs/InfoContent";
import PasswordContent from "./tabs/PasswordContent";
import SettingsContent from "./tabs/SettingsContent";
import { useTranslation } from "react-i18next";

interface ProfileDrawerProps {
    open: boolean;
    onClose: () => void;
}

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({ open, onClose }) => {
    const [activeKey, setActiveKey] = useState<string>('info');
    const [infoForm] = OpForm.useForm();
    const [passwordForm] = OpForm.useForm();
    const [settingsForm] = OpForm.useForm();

    const confirmModal = useConfirmModal();
    const { t } = useTranslation();

    useEffect(() => {
        if (open) {
            setActiveKey('info');
            // Reset forms whenever the drawer opens to ensure they start fresh
            infoForm.resetFields();
            passwordForm.resetFields();
            settingsForm.resetFields();
        }
    }, [open, infoForm, passwordForm, settingsForm]);

    // Called when we close the drawer by clicking cancel button or touching the main page
    const finalOnClose = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (infoForm.isFieldsTouched() || passwordForm.isFieldsTouched() || settingsForm.isFieldsTouched()) {
            confirmModal({
                title: t('confirmDiscard.title'),
                content: t('confirmDiscard.message'),
                okText: t('confirmDiscard.ok'),
                cancelText: t('confirmDiscard.cancel'),
                onOk: () => {
                    infoForm.resetFields();
                    passwordForm.resetFields();
                    settingsForm.resetFields();
                    onClose();
                },
            });
        } else {
            onClose();
        }
    };

    const items = [
        {
            key: 'info',
            label: t('profile.tabs.info'),
            children: <InfoContent form={infoForm} onClose={onClose} />,
        },
        {
            key: 'password',
            label: t('profile.tabs.password'),
            children: <PasswordContent form={passwordForm} onClose={onClose} />,
        },
        {
            key: 'settings',
            label: t('profile.tabs.settings'),
            children: <SettingsContent form={settingsForm} />,
        },
    ];

    return (
        <OpDrawer
            title={t('profile.title')}
            width={DRAWER_WIDTH}
            open={open}
            onClose={finalOnClose}
        >
            <OpTabs
                activeKey={activeKey}
                onChange={(newActiveKey: string) => {
                    setActiveKey(newActiveKey);
                }}
                items={items}
            />
        </OpDrawer>
    );
};

export default ProfileDrawer;